import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, ShieldCheck, Search, Landmark, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLAG = { IN: "🇮🇳", US: "🇺🇸", GB: "🇬🇧", AE: "🇦🇪", SG: "🇸🇬" };
const PARTNERS = [
  ["MeitY", "Ministry of Electronics & IT"],
  ["GeM", "Government e-Marketplace"],
  ["NIC", "National Informatics Centre"],
  ["MoF", "Ministry of Finance"],
  ["NSDL", "National Securities Depository"],
  ["UTITSL", "UTI Infrastructure Tech."],
  ["IOUX", "Indian Online Utility Exchange"],
  ["MSME", "Ministry of MSME"],
];

export default function BoardRegistryPage() {
  const { seal } = useParams();
  const nav = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(!!seal);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!seal) return;
    setLoading(true); setError(null);
    axios.get(`${API}/board-registry/${seal}`)
      .then((r) => setRecord(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Seal not found on the Vinci Board Registry."))
      .finally(() => setLoading(false));
  }, [seal]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      {/* Aurora */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-[130px] opacity-30" style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 70%)" }} />
        <div className="absolute -bottom-52 -right-52 w-[820px] h-[820px] rounded-full blur-[150px] opacity-25" style={{ background: "radial-gradient(circle,#8B7333 0%,transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><path d=%22M30 0v60M0 30h60%22 stroke=%22%23C9A961%22 stroke-width=%220.3%22/></svg>')" }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-14 md:py-20">
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <Landmark size={20} className="text-[#C9A961]" />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Vinci Inc. · Board Registry</span>
        </Link>

        {/* Search */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-sm border border-[#C9A961]/25 bg-[#0f0d0a] p-6 mb-8">
          <form onSubmit={(e) => { e.preventDefault(); if (query.trim()) nav(`/board-registry/${query.trim()}`); }}
            className="flex flex-wrap items-center gap-3">
            <Search size={18} className="text-[#C9A961]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid="registry-query"
              placeholder="Enter a Vinci Board Seal (e.g. VN-IND-XXXXXXXX)"
              className="flex-1 min-w-[240px] bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
            <button type="submit" data-testid="registry-search"
              className="rounded-sm bg-[#C9A961] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors">
              Verify
            </button>
          </form>
          <p className="font-body text-xs text-[#8A8A82] mt-3">
            Scan any QR on a Vinci Board document or paste the printed seal.
          </p>
        </motion.div>

        {loading && (
          <div className="rounded-sm border border-[#C9A961]/25 bg-[#0f0d0a] p-16 flex items-center gap-4 justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-[#C9A961]/30 border-t-[#C9A961] animate-spin" />
            <span className="font-grotesk text-xs uppercase tracking-[0.3em] text-[#C9A961]">Verifying with the Board…</span>
          </div>
        )}

        {error && !loading && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            data-testid="registry-error"
            className="rounded-sm border-2 border-[#C0392B]/40 bg-[#C0392B]/[0.08] p-10 text-center">
            <XCircle size={40} className="mx-auto text-[#C0392B]" />
            <h2 className="font-serif text-2xl mt-4 text-[#F5F5F0]">Not on the Board Registry</h2>
            <p className="font-body text-sm text-[#C9C9C2] mt-2">{error}</p>
            <p className="font-body text-xs text-[#8A8A82] mt-4 italic">
              If you believe this seal is authentic, contact the Vinci Board Secretariat.
            </p>
          </motion.div>
        )}

        {record && !loading && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="registry-card"
            className="rounded-sm border-2 border-[#C9A961]/50 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#0a0806] overflow-hidden relative">
            {/* Auth banner */}
            <div className="flex items-center gap-3 px-6 md:px-8 py-4 bg-[#2FB999]/[0.08] border-b border-[#2FB999]/30">
              <CheckCircle2 size={20} className="text-[#2FB999]" />
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#2FB999]">
                  · Authenticated · Vinci Board Registry ·
                </div>
                <div className="font-body text-xs text-[#8A8A82] mt-0.5">
                  Seal <span className="text-[#F5F5F0] font-mono">{record.seal}</span> is recorded and current.
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-[220px_1fr] gap-8 p-8 md:p-12 items-center">
              {/* Portrait / monogram */}
              <div className="relative mx-auto md:mx-0">
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-full border-2 border-[#C9A961]/60 grid place-items-center bg-[#0a0806] overflow-hidden"
                     style={{ boxShadow: "0 0 40px rgba(201,169,97,0.35)" }}>
                  <span className="font-serif text-5xl md:text-6xl text-[#C9A961]">
                    {(record.name || "V").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </span>
                </div>
                <span className="absolute -bottom-1 -right-1 w-11 h-11 rounded-full grid place-items-center bg-[#0a0806] border border-[#C9A961]/70 text-2xl">
                  {FLAG[record.country] || "🏳️"}
                </span>
              </div>

              <div className="text-center md:text-left">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">
                  · Level 0 · Founding Director · Seated ·
                </span>
                <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#F5F5F0] mt-3">
                  {record.name}
                </h1>
                {record.business_name && (
                  <p className="font-body text-[#8A8A82] mt-2">{record.business_name}</p>
                )}
                <div className="mt-6 grid grid-cols-2 gap-4 max-w-md mx-auto md:mx-0">
                  <Stat label="Cap-Table Stake" value={`${record.shareholding_pct.toFixed(2)}%`} />
                  <Stat label="Vinci Code" value={record.vinci_code || "—"} />
                  {record.authorized_at && (
                    <Stat label="Authorized" value={new Date(record.authorized_at).toLocaleDateString()} />
                  )}
                  <Stat label="Document"
                    value={record.kind === "induction" ? "Induction Certificate" : "Chairman's Letter"} />
                </div>
              </div>
            </div>

            {/* Partner strip */}
            <div className="border-t border-[#C9A961]/20 bg-black/40 px-6 md:px-8 py-6">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-[#C9A961] mb-4">
                · Associate & Technical Partners ·
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
                {PARTNERS.map(([short, long]) => (
                  <div key={short} className="text-center">
                    <div className="mx-auto w-14 h-14 rounded-full border border-[#C9A961]/50 grid place-items-center font-grotesk text-[10px] uppercase tracking-[0.05em] text-[#C9A961]"
                      style={{ boxShadow: "inset 0 0 0 3px rgba(201,169,97,0.08)" }}>
                      {short}
                    </div>
                    <div className="font-body text-[9px] text-[#8A8A82] mt-2 leading-tight">
                      {long}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer meta */}
            <div className="border-t border-[#C9A961]/15 bg-black/40 px-6 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]">
                · Vinci Inc. India · Board Registry · MMXXVI ·
              </span>
              <span className="font-mono text-[10px] text-[#C9A961]">{record.seal}</span>
            </div>
          </motion.div>
        )}

        {!seal && !loading && !record && !error && (
          <div className="text-center py-16">
            <ShieldCheck size={40} className="mx-auto text-[#C9A961]" />
            <p className="font-body text-[#8A8A82] mt-4">
              Enter a Vinci Board Seal above to verify any Induction Certificate,
              Chairman's Letter, or Shareholding Certificate.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-[#C9A961]/40 pl-4 text-left">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">{label}</div>
      <div className="font-serif text-lg text-[#F5F5F0] mt-1 tabular-nums">{value}</div>
    </div>
  );
}
